import type {
  ResolvedAuxiliaryScreen,
  ResolvedModule,
  ResolvedSection,
} from './resolve-navigation';

/**
 * Which module and which section the current URL is in — one pure answer, four consumers.
 *
 * The rail needs it to know which module to mark current and whose sections to show; the
 * breadcrumb needs it to name the row; the bottom bar needs it to mark its tab. Deriving it once
 * here is what keeps them from disagreeing, which is the failure mode of the pattern where each
 * consumer runs its own `pathname.startsWith(...)`.
 *
 * `NavLink` computes its OWN active state and would be enough for the rail alone. It is not enough
 * for the rest: the breadcrumb has no link to hang off, the flyout has to be decided for a module
 * that is not itself the current link, and the bottom bar marks a module by its territory rather
 * than by its landing path. So the rail takes its `aria-current` from this function too — one
 * source, and `NavLink` is used for navigation and prefetching only.
 */

export type ActiveLocation = {
  readonly module?: ResolvedModule;
  readonly section?: ResolvedSection;
  /** A section the gates hide from the strips, reached by address: it names the header, marks nothing. */
  readonly hiddenSection?: ResolvedSection;
  readonly auxiliary?: ResolvedAuxiliaryScreen;
};

/**
 * Prefix match on whole path SEGMENTS, never on characters. `/financeX` must not count as being
 * inside `/finance`, and `pathname.startsWith('/finance')` says it does — which is the same class
 * of error as the workspace-domain check that once matched `attacker@dgbiotech.eu.evil.example`.
 */
export function matchesPath(pathname: string, path: string, end: boolean): boolean {
  const here = normalizePathname(pathname);
  const target = normalizePathname(path);
  if (here === target) {
    return true;
  }
  if (end) {
    return false;
  }
  return here.startsWith(target === '/' ? '/' : `${target}/`);
}

/** Drops a trailing slash so `/finance/` and `/finance` are the same place. The root keeps its. */
function normalizePathname(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith('/')) {
    return pathname.slice(0, -1);
  }
  return pathname;
}

/**
 * The longest matching path wins, so a nested territory beats an ancestor's. Without it `/` — which
 * every path starts with — would claim every screen; it is `end: true` today, and relying on that
 * flag alone would make the ordering of the catalog load-bearing, which is exactly the kind of
 * invisible coupling this model exists to remove.
 */
function longestMatch<T>(
  candidates: readonly T[],
  pathOf: (candidate: T) => string,
): T | undefined {
  return candidates.reduce<T | undefined>((best, candidate) => {
    if (best === undefined) {
      return candidate;
    }
    return pathOf(candidate).length > pathOf(best).length ? candidate : best;
  }, undefined);
}

export function findActiveLocation(
  modules: readonly ResolvedModule[],
  auxiliaryScreens: readonly ResolvedAuxiliaryScreen[],
  pathname: string,
): ActiveLocation {
  const module = longestMatch(
    modules.filter((candidate) => matchesPath(pathname, candidate.basePath, candidate.end)),
    (candidate) => candidate.basePath,
  );

  if (module === undefined) {
    const auxiliary = longestMatch(
      auxiliaryScreens.filter((candidate) => matchesPath(pathname, candidate.basePath, false)),
      (candidate) => candidate.basePath,
    );
    return auxiliary === undefined ? {} : { auxiliary };
  }

  const section = longestMatch(
    module.sections.filter((candidate) => matchesPath(pathname, candidate.path, candidate.end)),
    (candidate) => candidate.path,
  );

  // A screen inside a module that is not a section of its own — a study detail, a participant
  // calendar — resolves to its module with no section. The breadcrumb then shows the module alone,
  // which is true, rather than the wrong section, which the old flat rail could not even express.
  if (section !== undefined) {
    return { module, section };
  }
  const hiddenSection = longestMatch(
    (module.hiddenSections ?? []).filter((candidate) =>
      matchesPath(pathname, candidate.path, candidate.end),
    ),
    (candidate) => candidate.path,
  );
  return hiddenSection === undefined ? { module } : { module, hiddenSection };
}
